import { type VFC, useMemo, useCallback, type ReactNode } from 'react';
import styled from 'styled-components';
import { Icon } from '@/components/icon';
import type { ItemType as BreadcrumbItemType } from 'antd/lib/breadcrumb/Breadcrumb';
import { Link } from 'react-router-dom';
import { PageHeader as AntdPageHeader, type PageHeaderProps as AntPageHeaderProps } from '@ant-design/pro-layout';
import type { StrictOmit } from '@enmon/commons';

const StyledPageHeader = styled(AntdPageHeader)`
  border-radius: 2px;
  margin: 0;

  &&& {
    // home icon align center
    .anticon svg {
      display: block;
    }
    .ant-breadcrumb a {
      color: ${({ theme }) => theme.color.theme.primary};
      text-decoration: underline;
    }
    .ant-page-header-heading-title {
      font-size: 1.4rem;
    }
    .ant-breadcrumb-link {
      word-break: break-word;
    }
    .ant-tabs-nav {
      margin: 0;
    }
  }

  background-color: #fff;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export type BreadcrumbRoute = BreadcrumbItemType & {
  dataCy?: string;
};

export interface PageHeaderProps extends StrictOmit<AntPageHeaderProps, 'breadcrumb'> {
  breadcrumbs?: BreadcrumbItemType[];
}

export const PageHeader: VFC<PageHeaderProps> = props => {
  const { breadcrumbs } = props;

  const breadcrumbHomeConfig = useMemo(
    (): BreadcrumbItemType => ({
      breadcrumbName: 'Home',
      path: '/',
    }),
    [],
  );

  const breadcrumbRenderer = useCallback(
    (route: BreadcrumbItemType): ReactNode =>
      route.path ? (
        <Link to={route.path}>{route.path === '/' ? <Icon iconString="home" /> : route.breadcrumbName}</Link>
      ) : (
        <span>{route.breadcrumbName}</span>
      ),
    [],
  );

  return (
    <StyledPageHeader
      {...props}
      // show breadcrumbs only if user is logged in to the customer
      {...(breadcrumbs
        ? {
            breadcrumb: {
              items: [breadcrumbHomeConfig, ...breadcrumbs],
              itemRender: breadcrumbRenderer,
            },
          }
        : {})}
    />
  );
};
